/**
 * cat-tag-adapter.ts — 分类 / 标签 DTO → 原型行形状的接真适配层
 *
 * 真 API 是 snake_case 规范化 DTO（CategoryResponse / TagResponse，name 走 name_i18n jsonb），
 * 原型页（BooksPage 的 CategoriesTags / CategoryEditor）消费的是 camelCase 富集行
 * `{ id, name, nameEn, books, langs, sort }`。本文件把 DTO 塑形成该行，并暴露
 * buildI18nNameMap 供 books-adapter 用 category_id / tag id 反查中英文名。
 *
 * 字段说明：
 *   - books（关联书籍数）：**已接真**——DTO 的 book_count（server LEFT JOIN novels 出书组去重计数）直接透传。
 *   - langs（该分类已维护的多语言版本）：DTO 只有 name_i18n 一份 map，无「按 system_languages
 *     枚举的启用语言对象」概念；这里不臆造，由页面用 name_i18n 的 key 集合自行派生展示。
 */

import type { CategoryResponse, TagResponse } from '@novel-admin/shared';

/** 从 name_i18n 取展示名：优先 zh，回退 en（zh/en 由后端 I18nFieldsSchema 保证至少其一存在）。 */
function pickName(nameI18n: Record<string, string>): string {
  return nameI18n.zh || nameI18n.en || '';
}

/** 分类 / 标签共用的原型行形状（逐字段对应 CategoriesTags 渲染 + CategoryEditorItem 录入）。 */
export interface CatTagRow {
  /** DTO id（UUID v7），用于 N+1 / 反查 */
  id: string;
  /** slug（DTO 透传，编辑去重 / URL 用） */
  slug: string;
  /** 展示名：name_i18n.zh || name_i18n.en */
  name: string;
  /** 英文名：name_i18n.en（缺则空串，与原型 nameEn 一致） */
  nameEn: string;
  /** name_i18n 已录入的全部 locale code（页面据此展示「多语言版本」徽标） */
  locales: string[];
  /**
   * **完整** `name_i18n`（不只 zh/en）。编辑面必须拿它初始化 —— PATCH 的 `name_i18n` 是
   * **整份替换**，只回填 zh/en 就等于把其余语言（典型：自动翻译补齐的 fr/cs）整片删掉，
   * 而且删的时候界面把它们渲染成空行、看着像「还没维护」。
   */
  nameI18n: Record<string, string>;
  /** 关联书籍数（接真）：server LEFT JOIN novels 出书组去重计数（DTO.book_count）；类型保留 ?null 以兼容页面 `?? '—'` */
  books: number | null;
  /**
   * A6 §14.2 / §14.4 乐观并发令牌（`expected_updated_at` 的来源，逐字回传、保留亚秒精度）。
   * **不进渲染**，只在编辑面提交时原样带回 —— `name_i18n` 按全量快照提交，没有它时两个运营
   * 各开一个语言 Tab 编辑会互相整片覆盖。
   */
  updatedAt: string;
}

/** CategoryResponse → 原型分类行。display_order 即原型的 sort（这里并入 row 由列表整体排序，不单列字段）。 */
export function adaptCategory(dto: CategoryResponse): CatTagRow {
  return {
    id: dto.id,
    slug: dto.slug,
    name: pickName(dto.name_i18n),
    nameEn: dto.name_i18n.en ?? '',
    locales: Object.keys(dto.name_i18n),
    nameI18n: dto.name_i18n,
    // book_count 接真：server LEFT JOIN novels 出该分类的书组去重计数（单查询，无 N+1）
    books: dto.book_count,
    updatedAt: dto.updated_at,
  };
}

/** TagResponse → 原型标签行。category_hint（所属分类提示）页面暂不展示，故不并入 row 以免脏字段。 */
export function adaptTag(dto: TagResponse): CatTagRow {
  return {
    id: dto.id,
    slug: dto.slug,
    name: pickName(dto.name_i18n),
    nameEn: dto.name_i18n.en ?? '',
    locales: Object.keys(dto.name_i18n),
    nameI18n: dto.name_i18n,
    // book_count 接真：含该标签的书组去重计数（同 adaptCategory）
    books: dto.book_count,
    updatedAt: dto.updated_at,
  };
}

/** buildI18nNameMap 单条值：zh / en 原文 + 展示名（zh||en），供 books-adapter 按 id 查名。 */
export interface I18nNameEntry {
  zh: string;
  en: string;
  /** 展示名：zh || en */
  name: string;
}

/**
 * buildI18nNameMap — 把分类 / 标签 DTO 列表索引成 id → { zh, en, name } map。
 *
 * books-adapter 拿到 NovelResponse.category_id / tags(UUID[]) 后，用本 map 反查可读名
 * （原型 BookRow.category / tags 是中文名字符串，真 API 只给 id）。
 * 接受 Category 与 Tag 混合（两者都有 id + name_i18n），统一签名。
 */
export function buildI18nNameMap(
  rows: ReadonlyArray<CategoryResponse | TagResponse>,
): Record<string, I18nNameEntry> {
  const map: Record<string, I18nNameEntry> = {};
  for (const row of rows) {
    const zh = row.name_i18n.zh ?? '';
    const en = row.name_i18n.en ?? '';
    map[row.id] = { zh, en, name: zh || en };
  }
  return map;
}
